import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  useDisclosure,
  useMediaQuery,
  useToast
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getFromUrl, serverRequest } from '../../api/Api';
import { OutlineButton, PrimaryButton } from '../../components/Buttons/Buttons';
import CounsellorsTable from '../../components/Cases/CounsellorsTable';
import DetailsItem from '../../components/Cases/DetailsItem';
import SeverityTag from '../../components/Cases/SeverityTag';
import SymptomsTag from '../../components/Cases/SymptomsTag';
import { SearchInput, TextField } from '../../components/Inputs/TextField';
import ModalView from '../../components/Modal';
import { Body, Headline } from '../../components/Texts/Texts';
import DashboardLayout from '../../layouts/dashboard/DashboardLayout';
import { colors } from '../../theme/colors';
import { formatAgeGroupForDisplay } from '../../utils/ageGroup';
import { getCurrentRole, UserRole } from '../../constants/roles';
import SafetyCasePanel from '../../components/Cases/SafetyCasePanel';
import {
  CasesListState,
  loadCasesListItems,
  resolveCasesListState,
  saveCasesListItems,
  saveCasesListState
} from '../../utils/casesListState';

interface Indexable {
  [key: string]: any;
}

const Cases = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id: caseId } = useParams();
  const location: any = useLocation();
  const listState: CasesListState = resolveCasesListState(location.state);
  const itemsFromState = Array.isArray(location.state?.items)
    ? location.state.items
    : [];
  const caseItems =
    itemsFromState.length > 0 ? itemsFromState : loadCasesListItems();
  const indexFromState =
    typeof location.state?.index === 'number' ? location.state.index : -1;

  const handleBackToCases = () => {
    saveCasesListState(listState);
    navigate('/cases', {
      state: listState
    });
  };

  const currentIndex = (() => {
    if (
      indexFromState >= 0 &&
      caseItems[indexFromState]?._id === caseId
    ) {
      return indexFromState;
    }
    const matched = caseItems.findIndex((item: any) => item?._id === caseId);
    return matched >= 0 ? matched : 0;
  })();

  const prevCaseId =
    currentIndex > 0 ? caseItems[currentIndex - 1]?._id : null;
  const nextCaseId =
    currentIndex >= 0 && currentIndex < caseItems.length - 1
      ? caseItems[currentIndex + 1]?._id
      : null;

  const handleNavigateSibling = (
    siblingId: string | null,
    siblingIndex: number
  ) => {
    if (!siblingId) {
      return;
    }
    saveCasesListItems(caseItems);
    navigate(`/cases/${siblingId}`, {
      state: {
        items: caseItems,
        index: siblingIndex,
        ...listState
      }
    });
  };

  const [caseItem, setCaseItem] = useState<any>(null);
  const [isSmallerThan1280] = useMediaQuery('(max-width: 1280px)');
  const [counsellors, setCounsellors] = useState([]);
  const [counsellorId, setCounsellorId] = useState('');
  const [pickLoading, setPickLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isReferOpen,
    onOpen: onReferOpen,
    onClose: onReferClose
  } = useDisclosure();
  const {
    isOpen: isForceCloseOpen,
    onOpen: onForceCloseOpen,
    onClose: onForceCloseClose
  } = useDisclosure();
  const [referralPartners, setReferralPartners] = useState<any[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [referralReason, setReferralReason] = useState('');
  const [referralNotes, setReferralNotes] = useState('');
  const [referLoading, setReferLoading] = useState(false);
  const [forceCloseLoading, setForceCloseLoading] = useState(false);
  const currentRole = getCurrentRole();
  const canReferToPartner =
    currentRole === UserRole.ADMIN ||
    currentRole === UserRole.SUPER_ADMIN ||
    currentRole === UserRole.SUPERVISOR;
  const canForceClose =
    currentRole === UserRole.SUPER_ADMIN ||
    currentRole === UserRole.SUPERVISOR;
  const canPickCase =
    currentRole === UserRole.SUPER_ADMIN ||
    currentRole === UserRole.SUPERVISOR;
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filter] = useState('');
  const [counsellorsCount, setCounsellorsCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchResults, setSearchResults] = useState<any>([]);
  const PAGE_SIZE = 10;
  const [timeoutId, setTimeoutId] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const getCase = async (id: string) => {
    try {
      const data = await getFromUrl(`cases/${id}`);
      setCaseItem(data);
    } catch (e: any) {
      console.error(e.message);
    }
  };

  const loadReferralPartners = async () => {
    try {
      const partners = await getFromUrl('referral/all');
      if (partners !== undefined) {
        setReferralPartners(partners);
      }
    } catch (e: any) {
      console.error(e.message);
    }
  };

  const handleOpenReferModal = () => {
    loadReferralPartners().catch(() => {
      return;
    });
    setSelectedPartnerId('');
    setReferralReason('');
    setReferralNotes('');
    onReferOpen();
  };

  const referCaseToPartner = async () => {
    if (!selectedPartnerId || !referralReason.trim()) {
      toast({
        position: 'top',
        title: 'Error',
        description: 'Please select a partner and provide a referral reason',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setReferLoading(true);
    try {
      const response = await serverRequest({
        endpoint: `cases/${caseId}/refer`,
        method: 'POST',
        body: {
          referralPartnerId: selectedPartnerId,
          reason: referralReason,
          notes: referralNotes || undefined
        }
      });

      if (response?.status === 401) {
        navigate('/authentication/signin');
        return;
      }

      if (response?.status && response.status >= 400) {
        let errorMessage = 'Failed to refer case to partner';
        try {
          const errorBody = await response.json();
          errorMessage =
            errorBody?.message || errorBody?.error || errorMessage;
        } catch {
          // use default message
        }
        toast({
          position: 'top',
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }

      onReferClose();
      await getCase(caseId as string);
      toast({
        position: 'top',
        title: 'Success',
        description: 'Case referred to partner successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (e: any) {
      toast({
        position: 'top',
        title: 'Error',
        description: e.message || 'Failed to refer case to partner',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setReferLoading(false);
    }
  };

  const forceCloseCase = async () => {
    setForceCloseLoading(true);
    try {
      const response = await serverRequest({
        endpoint: `cases/${caseId}/force-close`,
        method: 'PUT',
        body: {}
      });

      if (response?.status === 401) {
        navigate('/authentication/signin');
        return;
      }

      if (response?.status && response.status >= 400) {
        let errorMessage = 'Failed to force close case';
        try {
          const errorBody = await response.json();
          errorMessage =
            errorBody?.message || errorBody?.error || errorMessage;
        } catch {
          // use default message
        }
        toast({
          position: 'top',
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }

      onForceCloseClose();
      await getCase(caseId as string);
      toast({
        position: 'top',
        title: 'Success',
        description: 'Case force-closed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (e: any) {
      toast({
        position: 'top',
        title: 'Error',
        description: e.message || 'Failed to force close case',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setForceCloseLoading(false);
    }
  };

  const pickCase = async () => {
    if (!canPickCase) {
      toast({
        position: 'top',
        title: 'Error',
        description: 'You are not allowed to pick a case',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    setPickLoading(true);
    try {
      // First, check if a session already exists for this case
      const existingSessions = await getFromUrl('cases/sessions');
      
      if (existingSessions && Array.isArray(existingSessions)) {
        // Look for an existing session for this case
        const existingSession = existingSessions.find((session: any) => {
          // Check multiple possible ways the case ID might be stored
          const sessionCaseId = session.case?._id || session.case || session._id;
          return sessionCaseId === caseId;
        });
        
        if (existingSession) {
          // Session already exists, navigate to it
          setPickLoading(false);
          toast({
            position: 'top',
            title: 'Existing Session Found',
            description: 'You already have an active session with this client. Redirecting to existing session.',
            status: 'info',
            duration: 3000,
            isClosable: true
          });
          navigate(`/sessions`, {
            state: { case: existingSession, activeTab: 'my sessions' }
          });
          return;
        }
      }
      
      // No existing session found, create a new one
      const response = await serverRequest({
        method: 'PUT',
        body: null,
        endpoint: `cases/pick/${caseId}`
      });
      setPickLoading(false);
      if (response) {
        toast({
          position: 'top',
          title: 'New Session Created',
          description: 'Starting a new session with this client.',
          status: 'success',
          duration: 2000,
          isClosable: true
        });
        navigate(`/sessions`, {
          state: { case: response, activeTab: 'my sessions' }
        });
      }
    } catch (e: any) {
      setPickLoading(false);
      console.error('Error picking case:', e);
      toast({
        position: 'top',
        title: 'Error',
        description: 'Failed to pick case. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const getCounsellors = async () => {
    try {
      const data = await getFromUrl(
        `users/counsellors?type=ALL&size=${PAGE_SIZE}&page=${currentPage}`
      );
      setCounsellors(data);
      setCounsellorsCount(data.length);
    } catch (e: any) {
      return;
    }
  };

  const assignCase = async () => {
    // Prevent assignment if case is closed
    if (caseItem?.stage === 'CLOSED') {
      toast({
        position: 'top',
        title: 'Error',
        description: 'Cannot reassign a closed case',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setAssignLoading(true);
    if (counsellorId) {
      const body = {
        caseId,
        counsellorId
      };
      try {
        const response = await serverRequest({
          method: 'PUT',
          body,
          endpoint: `cases/assign`
        });
        setAssignLoading(false);
        onClose();
        
        // Refresh case data to show updated counsellor name
        if (caseId) {
          await getCase(caseId);
        }
        
        toast({
          position: 'top',
          title: 'Success',
          description: `You've assigned case to ${response.counsellor.name}`,
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } catch (e: any) {
        setAssignLoading(false);
      }
    }
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const severityColors = {
    mild: colors.gray,
    moderate: colors.accent,
    moderately_severe: colors.warning,
    severe: colors.error
  };

  const getCaseStatus = (stage: string, status?: string) => {
    if (status === 'REFERRED') {
      return { text: 'Referred', color: colors.deep_blue };
    }
    switch (stage) {
      case 'ACTIVE':
        return { text: 'Active', color: colors.accent };
      case 'QUESTION_COMPLETED':
        return { text: 'Question Completed', color: colors.warning };
      case 'ONGOING':
        return { text: 'Ongoing', color: colors.deep_blue };
      case 'CLOSED':
        return { text: 'Closed', color: colors.error };
      default:
        return { text: stage || 'Unknown', color: colors.gray };
    }
  };

  useEffect(() => {
    if (!caseId) {
      return;
    }
    setCaseItem(null);
    getCase(caseId).catch((err) => console.error(err));
  }, [caseId]);

  const searchCounsellorsList = async () => {
    if (timeoutId) clearTimeout(timeoutId);
    const searchTimeoutId = setTimeout(async () => {
      try {
        const endpoint = `users/supervisors_counsellors/search?search=${searchKeyword}`;
        const searchResults = await getFromUrl(endpoint);
        setSearchResults(searchResults);
      } catch (e: any) {
        console.error(e.message);
      }
    }, 1000);
    setTimeoutId(searchTimeoutId);
  };

  useEffect(() => {
    if (searchKeyword.length) {
      searchCounsellorsList().catch((err) => console.error(err));
    }
  }, [searchKeyword]);

  useEffect(() => {
    getCounsellors().catch((err) => console.error(err));
  }, [currentPage]);

  return (
    <DashboardLayout pageTitle='Cases'>
      <Box pr={isSmallerThan1280 ? '50px' : '150px'}>
        <Flex
          display='inline-flex'
          onClick={handleBackToCases}
          alignItems='center'
          cursor='pointer'
          mb='40px'
          tabIndex={0}
          aria-label='Back to cases list'
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleBackToCases();
            }
          }}
        >
          <ChevronLeftIcon color='deep_blue' h='30px' w='16px' />
          <Body size='14px' textTransform='uppercase' color='deep_blue'>
            Back
          </Body>
        </Flex>
        {caseItem && (
          <Box>
            <Flex alignItems='center' gap='12px' mb='16px'>
              <SeverityTag type={caseItem.severity.toLowerCase()} />
              <Box
                px='12px'
                py='6px'
                borderRadius='6px'
                bg={getCaseStatus(caseItem.stage, caseItem.status).color + '20'}
                border={`1px solid ${getCaseStatus(caseItem.stage, caseItem.status).color}`}
              >
                <Body
                  size='12px'
                  fontWeight={600}
                  color={getCaseStatus(caseItem.stage, caseItem.status).color}
                >
                  {getCaseStatus(caseItem.stage, caseItem.status).text}
                </Body>
              </Box>
            </Flex>
            <Flex justifyContent='space-between'>
              <Box maxW={isSmallerThan1280 ? '400px' : '550px'} mr='20px'>
                <Headline
                  size='19px'
                  color='deep_blue'
                  fontWeight={700}
                  mt='16px'
                  mb='20px'
                >
                  {caseItem.user.name}
                </Headline>
                {caseItem?.counsellor && (
                  <Grid
                    gridTemplateColumns={`repeat(${
                      isSmallerThan1280 ? '2' : '3'
                    }, 1fr)`}
                    gap='18px'
                  >
                    <GridItem
                      display='flex'
                      justifyContent='space-between'
                      gridColumnGap='14px'
                      marginBottom='20px'
                    >
                      <Box>
                        <DetailsItem
                          label='Counsellor assigned'
                          value={caseItem.counsellor.name || ''}
                        />
                      </Box>
                    </GridItem>
                  </Grid>
                )}
                <Grid
                  gridTemplateColumns={`repeat(${
                    isSmallerThan1280 ? '2' : '3'
                  }, 1fr)`}
                  gap='18px'
                >
                  <GridItem
                    display='flex'
                    justifyContent='space-between'
                    gridColumnGap='14px'
                  >
                    <Box>
                      <DetailsItem
                        label='Gender:'
                        value={caseItem.user.gender || ''}
                      />
                    </Box>
                    <Box>
                      <DetailsItem
                        label='Age:'
                        value={formatAgeGroupForDisplay(caseItem.user.ageGroup)}
                      />
                    </Box>
                  </GridItem>
                  <GridItem>
                    <DetailsItem
                      label='Location:'
                      value={caseItem.user.address}
                    />
                  </GridItem>
                  <GridItem>
                    <DetailsItem
                      label='Employment Status:'
                      value={caseItem.user.employmentStatus}
                    />
                  </GridItem>
                  <GridItem colSpan={2}>
                    <DetailsItem
                      label='Phone Number:'
                      value={caseItem.user.phone}
                    />
                  </GridItem>
                </Grid>
                <Flex mt='20px' mb='14px'>
                  <SymptomsTag
                    text={caseItem.symptom}
                    color={
                      (severityColors as Indexable)[
                        caseItem.severity.toLowerCase()
                      ]
                    }
                    mr='16px'
                  />
                </Flex>
                {!caseItem?.isReported && (
                  <>
                    <Body size='16px' color='black' fontWeight={700} mb='5px'>
                      Problem Statement
                    </Body>
                    <Body size='16px' color='black' mb='5px'>
                      {caseItem.problemStatement}
                    </Body>
                  </>
                )}
                {caseItem.stage === 'CLOSED' && (
                  <Box
                    mt='24px'
                    p='20px'
                    borderRadius='8px'
                    bg='rgba(5, 57, 107, 0.06)'
                    border='1px solid'
                    borderColor='gray'
                  >
                    <Body size='16px' color='deep_blue' fontWeight={700} mb='12px'>
                      Counsellor Feedback
                    </Body>
                    <DetailsItem
                      label='Close Reason:'
                      value={caseItem.closeReason || 'N/A'}
                    />
                    <DetailsItem
                      label='Backstory:'
                      value={caseItem.backstory || 'N/A'}
                    />
                    <DetailsItem
                      label='Help Provided:'
                      value={caseItem.helpProvided || 'N/A'}
                    />
                    <DetailsItem
                      label='Overall Feedback:'
                      value={caseItem.overallFeedback || 'N/A'}
                    />
                    <DetailsItem
                      label='Session Number:'
                      value={
                        caseItem.sessionNumber != null
                          ? String(caseItem.sessionNumber)
                          : 'N/A'
                      }
                    />
                    <DetailsItem
                      label='Average Time on Session:'
                      value={caseItem.averageTimeOnSession || 'N/A'}
                    />
                    <DetailsItem
                      label='Counselling Team:'
                      value={caseItem.counsellingTeam || 'N/A'}
                    />
                    <DetailsItem
                      label='Client Filled Form:'
                      value={
                        caseItem.hasClientFilledForm === true
                          ? 'Yes'
                          : caseItem.hasClientFilledForm === false
                          ? 'No'
                          : 'N/A'
                      }
                    />
                    <DetailsItem
                      label='Residue Emotion:'
                      value={
                        caseItem.hasResidueEmotion === true
                          ? 'Yes'
                          : caseItem.hasResidueEmotion === false
                          ? 'No'
                          : 'N/A'
                      }
                    />
                    {caseItem.closedAt && (
                      <DetailsItem
                        label='Closed At:'
                        value={new Date(caseItem.closedAt).toLocaleString()}
                      />
                    )}
                  </Box>
                )}
                {caseItem.status === 'REFERRED' && (
                  <Box
                    mt='24px'
                    p='20px'
                    borderRadius='8px'
                    bg='rgba(5, 57, 107, 0.08)'
                    border='1px solid'
                    borderColor='deep_blue'
                  >
                    <Body size='16px' color='deep_blue' fontWeight={700} mb='12px'>
                      Partner Referral
                    </Body>
                    <DetailsItem
                      label='Partner:'
                      value={caseItem.referredPartnerId?.name || 'N/A'}
                    />
                    <DetailsItem
                      label='Partner Email:'
                      value={caseItem.referredPartnerId?.email || 'N/A'}
                    />
                    <DetailsItem
                      label='Referral Reason:'
                      value={caseItem.closeReason || 'N/A'}
                    />
                    {caseItem.referralNotes && (
                      <DetailsItem
                        label='Notes:'
                        value={caseItem.referralNotes}
                      />
                    )}
                    <DetailsItem
                      label='Referred On:'
                      value={
                        caseItem.referralEmailSentAt
                          ? new Date(caseItem.referralEmailSentAt).toLocaleString()
                          : 'N/A'
                      }
                    />
                  </Box>
                )}
                <SafetyCasePanel
                  caseItem={caseItem}
                  caseId={caseId as string}
                  currentRole={currentRole}
                  onUpdated={() => getCase(caseId as string)}
                />
                {caseItem.isReported && (
                  <>
                    <Body
                      backgroundColor='rgba(255, 113, 113, 0.1)'
                      p='24px'
                      minWidth='495px'
                      pb='79px'
                      borderRadius='5px'
                      mt='32px'
                    >
                      <Body
                        size='16px'
                        color='#FF7171'
                        mb='24px'
                        fontSize='19px'
                        fontWeight={700}
                      >
                        Reason for report:
                      </Body>
                      <Body>{caseItem?.reportReason}</Body>
                    </Body>
                  </>
                )}
              </Box>
              <Flex
                w='343px'
                flexDirection='column'
                justifyContent='space-between'
              >
                <Flex flexDirection='column'>
                  {canPickCase && (
                    <PrimaryButton
                      label={caseItem.isReported ? 'Resolve' : 'Pick case'}
                      onClick={pickCase}
                      isLoading={pickLoading}
                      height='55px'
                      background={
                        caseItem.isReported
                          ? '#389583'
                          : (severityColors as Indexable)[
                              caseItem.severity.toLowerCase()
                            ]
                      }
                      mb='10px'
                    />
                  )}
                  <OutlineButton
                    label={
                      caseItem.stage === 'CLOSED'
                        ? 'Case Closed'
                        : caseItem.isReported
                        ? 'Block'
                        : caseItem.counsellor
                        ? 'Re - Assign'
                        : 'Assign case'
                    }
                    onClick={caseItem.stage === 'CLOSED' ? undefined : onOpen}
                    height='55px'
                    disabled={caseItem.stage === 'CLOSED'}
                    color={
                      caseItem.stage === 'CLOSED'
                        ? colors.gray
                        : caseItem.isReported
                        ? '#FF7171'
                        : (severityColors as Indexable)[
                            caseItem.severity.toLowerCase()
                          ]
                    }
                  />
                  {canReferToPartner &&
                    caseItem.stage !== 'CLOSED' &&
                    !(
                      caseItem.isAtRisk ||
                      caseItem.isReported ||
                      caseItem.status === 'ESCALATED'
                    ) && (
                    <OutlineButton
                      label='Refer to Partner'
                      onClick={handleOpenReferModal}
                      height='55px'
                      mt='10px'
                      color={colors.deep_blue}
                    />
                  )}
                  {canForceClose && caseItem.stage !== 'CLOSED' && (
                    <OutlineButton
                      label='Force close'
                      onClick={onForceCloseOpen}
                      height='55px'
                      mt='10px'
                      color='#FF7171'
                    />
                  )}
                </Flex>
                <Flex justifyContent='space-between' mt='16px' gap='12px'>
                  <Button
                    variant='ghost'
                    onClick={() =>
                      handleNavigateSibling(prevCaseId, currentIndex - 1)
                    }
                    isDisabled={!prevCaseId}
                    aria-label='Previous case'
                    height='44px'
                    px='12px'
                  >
                    <ChevronLeftIcon />
                    <Text
                      fontWeight='400'
                      marginLeft='15px'
                      color='#05396B'
                      opacity={!prevCaseId ? '0.3' : '1'}
                    >
                      PREVIOUS
                    </Text>
                  </Button>
                  <Button
                    variant='outline'
                    borderColor='deep_blue'
                    onClick={() =>
                      handleNavigateSibling(nextCaseId, currentIndex + 1)
                    }
                    isDisabled={!nextCaseId}
                    aria-label='Next case'
                    height='44px'
                    px='12px'
                  >
                    <Text
                      fontWeight='400'
                      marginRight='15px'
                      color='#05396B'
                      opacity={!nextCaseId ? '0.3' : '1'}
                    >
                      NEXT
                    </Text>
                    <ChevronRightIcon />
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Box>
        )}
      </Box>
      <ModalView isOpen={isOpen} onClose={onClose} maxW='80%' bg='gray_50'>
        <Box maxH='750px' py='50px' px='146px'>
          <Flex alignItems='center' mb='28px'>
            <Headline size='19px' color='deep_blue' mr='auto'>
              Assign Case to a supervisor or Counsellor
            </Headline>
            <SearchInput
              maxW='244px'
              value={searchKeyword}
              onChange={({ target: { value } }) => setSearchKeyword(value)}
              mr='40px'
            />
          </Flex>
          {counsellorsCount && counsellors.length ? (
            <CounsellorsTable
              filterCounsellors={filter}
              counsellors={searchKeyword.length ? searchResults : counsellors}
              searchText={searchKeyword}
              counsellorsCount={counsellorsCount}
              currentPage={currentPage}
              onPageChange={onPageChange}
              scroll
              actionItem={({ counsellorData }: { counsellorData: any }) => (
                <Box
                  boxSize='20px'
                  borderRadius='5px'
                  borderWidth='2px'
                  borderColor='deep_blue'
                  mr='12px'
                  cursor='pointer'
                  bg={
                    counsellorData._id === counsellorId
                      ? 'deep_blue'
                      : 'transparent'
                  }
                  onClick={() => setCounsellorId(counsellorData._id)}
                ></Box>
              )}
            />
          ) : null}
          {searchKeyword.length && !searchResults.length ? (
            <Text mx='auto' w='max-content' color={colors.deep_blue} mt='40px'>
              No data found
            </Text>
          ) : null}
          {searchKeyword.length && !searchResults.length ? null : (
            <Flex justifyContent='center'>
              <PrimaryButton
                label='Assign'
                onClick={assignCase}
                height='55px'
                width='343px'
                background='deep_blue'
                mt='23px'
                mx='auto'
                isLoading={assignLoading}
                disabled={counsellorId === ''}
              />
            </Flex>
          )}
        </Box>
      </ModalView>
      <ModalView isOpen={isReferOpen} onClose={onReferClose} maxW='600px' bg='gray_50'>
        <Box py='40px' px='50px'>
          <Headline size='19px' color='deep_blue' mb='24px'>
            Refer Case to Partner
          </Headline>
          <Body size='14px' color='gray' mb='20px'>
            The partner will receive an email with the client&apos;s information
            and problem statement.
          </Body>
          <Box mb='16px'>
            <Body size='14px' color='deep_blue' mb='8px' fontWeight={600}>
              Partner *
            </Body>
            <Box
              as='select'
              w='100%'
              p='12px'
              borderRadius='5px'
              border='1px solid'
              borderColor='gray.200'
              bg='white'
              value={selectedPartnerId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedPartnerId(e.target.value)
              }
            >
              <option value=''>Select a partner</option>
              {referralPartners.map((partner: any) => (
                <option key={partner._id} value={partner._id}>
                  {partner.name}
                  {partner.email ? ` (${partner.email})` : ' (no email)'}
                </option>
              ))}
            </Box>
          </Box>
          <TextField
            label='Referral Reason *'
            value={referralReason}
            onChange={setReferralReason}
            bg='white'
            required
          />
          <TextField
            label='Additional Notes'
            value={referralNotes}
            onChange={setReferralNotes}
            bg='white'
          />
          <PrimaryButton
            label='Send Referral'
            onClick={referCaseToPartner}
            height='55px'
            width='100%'
            background='deep_blue'
            mt='16px'
            isLoading={referLoading}
            disabled={!selectedPartnerId || !referralReason.trim()}
          />
        </Box>
      </ModalView>
      <ModalView
        isOpen={isForceCloseOpen}
        onClose={onForceCloseClose}
        bg='gray_50'
        maxW='600px'
        noCloseBtn
      >
        <Box bg='gray_50' px='86px' py='53px' borderRadius='8px'>
          <Text fontSize='19px' color='gray' mb='28px' textAlign='center'>
            Are you sure you want to force close this case?
          </Text>
          <Flex justifyContent='center' gap='16px'>
            <Button
              onClick={forceCloseCase}
              isLoading={forceCloseLoading}
              bg='#FF7171'
              color='white'
              py='22px'
              px='32px'
              aria-label='Confirm force close'
            >
              YES
            </Button>
            <Button
              onClick={onForceCloseClose}
              isDisabled={forceCloseLoading}
              bg={colors.accent}
              py='22px'
              px='32px'
              color='white'
              aria-label='Cancel force close'
            >
              NO
            </Button>
          </Flex>
        </Box>
      </ModalView>
    </DashboardLayout>
  );
};

export default Cases;
